/**
 * Animated expand/collapse wrapper for nested custom-table row content.
 *
 * Uses measured pixel heights so collapse transitions remain smooth even when
 * nested content (e.g. DataGrid) is unmounted only after the animation ends.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

/** Matches {@link custom-table.scss} expandable row height transition duration. */
const EXPANDABLE_ROW_ANIMATION_MS = 450;

/** Props for {@link ExpandableRowContentPanel}. */
export interface ExpandableRowContentPanelProps {
  /** Logical expanded state from table row data. */
  expanded: boolean;
  /** Column span for the expandable content row. */
  columnCount: number;
  /** Left accent border color applied while expanded. */
  accentColor?: string;
  /** Optional bottom border for the nested content body. */
  borderBottom?: string;
  /** Nested content rendered inside the animated panel. */
  children: React.ReactNode;
}

/**
 * Returns whether the user prefers reduced motion.
 *
 * @returns True when reduced-motion is requested
 */
const prefersReducedMotion = (): boolean =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Renders an expandable table row with smooth open/close animation.
 *
 * @param props - Panel configuration and nested content
 * @returns Animated expandable `<tr>` element
 */
export const ExpandableRowContentPanel: React.FC<
  ExpandableRowContentPanelProps
> = ({
  expanded,
  columnCount,
  accentColor = "transparent",
  borderBottom,
  children,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [shouldRenderContent, setShouldRenderContent] = useState(expanded);
  const [isExpandedVisually, setIsExpandedVisually] = useState(expanded);
  const [panelHeight, setPanelHeight] = useState<number | "auto">(
    expanded ? "auto" : 0,
  );

  useEffect(() => {
    if (expanded) {
      setShouldRenderContent(true);
    }
  }, [expanded]);

  useLayoutEffect(() => {
    const panel = panelRef.current;

    if (!shouldRenderContent || !panel) {
      return undefined;
    }

    if (prefersReducedMotion()) {
      setPanelHeight(expanded ? "auto" : 0);
      setIsExpandedVisually(expanded);
      if (!expanded) {
        setShouldRenderContent(false);
      }
      return undefined;
    }

    if (expanded) {
      setIsExpandedVisually(false);
      setPanelHeight(0);

      const openFrameId = window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          setPanelHeight(panel.scrollHeight);
          setIsExpandedVisually(true);
        });
      });

      return () => window.cancelAnimationFrame(openFrameId);
    }

    const currentHeight = panel.scrollHeight;
    setPanelHeight(currentHeight);
    setIsExpandedVisually(false);

    const collapseFrameId = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        setPanelHeight(0);
      });
    });

    return () => window.cancelAnimationFrame(collapseFrameId);
  }, [expanded, shouldRenderContent]);

  useEffect(() => {
    if (expanded || !shouldRenderContent || isExpandedVisually) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setShouldRenderContent(false);
    }, EXPANDABLE_ROW_ANIMATION_MS + 50);

    return () => window.clearTimeout(timeoutId);
  }, [expanded, isExpandedVisually, shouldRenderContent]);

  /**
   * Finalizes open/close state after the height transition completes.
   *
   * @param event - Transition event from the animated panel wrapper
   */
  const handlePanelTransitionEnd = (
    event: React.TransitionEvent<HTMLDivElement>,
  ): void => {
    if (event.target !== event.currentTarget) {
      return;
    }

    if (event.propertyName !== "height") {
      return;
    }

    if (expanded) {
      setPanelHeight("auto");
      return;
    }

    setShouldRenderContent(false);
    setPanelHeight(0);
  };

  return (
    <tr
      className={`expandable-row-content ${isExpandedVisually ? "is-expanded" : ""}`.trim()}
      style={
        {
          display: "block",
          width: "100%",
          "--expandable-row-accent-color": accentColor,
        } as React.CSSProperties
      }
      aria-hidden={!expanded && !shouldRenderContent}
    >
      <td
        colSpan={columnCount}
        style={{
          padding: 0,
          border: "none",
          display: "block",
          width: "100%",
        }}
      >
        <div className="expandable-row-content__grid">
          <div
            ref={panelRef}
            className="expandable-row-content__panel"
            style={{
              height: panelHeight === "auto" ? "auto" : `${panelHeight}px`,
            }}
            onTransitionEnd={handlePanelTransitionEnd}
          >
            <div
              className="expandable-row-content__body"
              style={{ borderBottom }}
            >
              {shouldRenderContent ? children : null}
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
};
